/**
 * RED-317 PR 3b — the Word paste transform: what Word puts on the clipboard
 * (HTML in Word's own dialect) rewritten into plain HTML that the Studio
 * schema's parse rules read into the PR 3 contract attrs
 * (.claude/tickets/RED-317/pr3-contract.md):
 *
 * - bold / italic / underline / strike stay the <b> <i> <u> <s> Word writes
 *   (the Tiptap marks parse those);
 * - a run's colour and highlight become `data-ooxml-color` (6-hex) and
 *   `data-ooxml-highlight` (an ST_HighlightColor name) on a span: the
 *   ooxmlRun mark's deltas;
 * - a paragraph's alignment becomes `data-ooxml-align`: the textAlign delta;
 * - Word's list paragraphs (`mso-list:l0 level2 lfo1`, its marker glyph in
 *   a `<![if !supportLists]>` island, or Mac Word's <ol><li>) become nested
 *   <ul>/<ol> — the kind from the <style> block's `@list l0:level2`, the
 *   level from the paragraph;
 * - tables keep their colspan/rowspan; a cell's fill becomes
 *   `data-ooxml-fill`, its width a `colwidth` (new tables are gridded in
 *   proportion to the first row's), and a <thead> row becomes <th> cells
 *   (a repeated header row, w:tblHeader);
 * - a picture travels only as `<img data-studio-picture="{attrs}">` after it
 *   was uploaded (Mac Word inlines pictures and charts as data: URIs, see
 *   wordPictures()); Word's file:// pictures cannot be read by a web page
 *   and are dropped and counted, so the editor can say so;
 * - footnote and comment bodies, VML and Word's other islands go.
 *
 * Pure DOM, no imports: tests/js/templateStudio/wordPaste.test.js loads this
 * file as is into Chromium and runs it on real Word clipboard HTML.
 */

/** Pictures the media endpoint accepts (contract §5; it sniffs the bytes again). */
const PICTURE_TYPES = ['image/png', 'image/jpeg', 'image/gif']

/** CSS colour names Word writes → 6-hex. Black and windowtext are "automatic": no delta. */
const NAMED_COLORS = {
  red: 'FF0000',
  blue: '0000FF',
  yellow: 'FFFF00',
  lime: '00FF00',
  green: '008000',
  aqua: '00FFFF',
  cyan: '00FFFF',
  fuchsia: 'FF00FF',
  magenta: 'FF00FF',
  navy: '000080',
  teal: '008080',
  purple: '800080',
  maroon: '800000',
  olive: '808000',
  gray: '808080',
  grey: '808080',
  silver: 'C0C0C0',
  white: 'FFFFFF',
  orange: 'FFA500',
}

const AUTOMATIC_COLORS = ['black', 'windowtext', 'auto', 'inherit', '000000']

/** mso-highlight (CSS names) → ST_HighlightColor (contract §1). */
const HIGHLIGHTS = {
  yellow: 'yellow',
  lime: 'green',
  aqua: 'cyan',
  cyan: 'cyan',
  fuchsia: 'magenta',
  magenta: 'magenta',
  blue: 'blue',
  red: 'red',
  navy: 'darkBlue',
  teal: 'darkCyan',
  green: 'darkGreen',
  purple: 'darkMagenta',
  maroon: 'darkRed',
  olive: 'darkYellow',
  gray: 'darkGray',
  grey: 'darkGray',
  silver: 'lightGray',
  black: 'black',
  white: 'white',
}

const ALIGNMENTS = { left: 'left', start: 'left', center: 'center', right: 'right', end: 'right', justify: 'justify', both: 'justify' }

const INLINE_TAGS = ['SPAN', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'DEL', 'SUP', 'SUB', 'FONT', 'A']

const defaultParse = (html) => new DOMParser().parseFromString(html, 'text/html')

/** Is this clipboard HTML from Word (Windows or Mac)? */
export function isWordHtml(html) {
  return typeof html === 'string' && /urn:schemas-microsoft-com:office:(?:word|office)|<meta[^>]+(?:Word\.Document|Microsoft Word)|class=["']?Mso|mso-list:|mso-highlight:/i.test(html)
}

/** The value of one property in a raw style attribute (Word's styles are not all CSS). */
function styleValue(element, property) {
  const style = element.getAttribute?.('style')
  if (!style) return null
  const match = new RegExp(`(?:^|;)\\s*${property}\\s*:\\s*([^;]+)`, 'i').exec(style.replace(/\s+/g, ' '))
  return match ? match[1].trim() : null
}

/** 'auto'/black → null (the style decides), a colour → 6-hex upper-case, else null. */
export function cssColorToHex(value) {
  if (!value) return null
  const color = value.trim().toLowerCase()
  if (AUTOMATIC_COLORS.includes(color)) return null
  if (/^#[0-9a-f]{6}$/.test(color)) return color === '#000000' ? null : color.slice(1).toUpperCase()
  if (/^#[0-9a-f]{3}$/.test(color)) {
    const hex = color
      .slice(1)
      .split('')
      .map((digit) => digit + digit)
      .join('')
      .toUpperCase()
    return hex === '000000' ? null : hex
  }
  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/.exec(color)
  if (rgb) {
    const hex = rgb
      .slice(1, 4)
      .map((channel) => Math.min(255, Number(channel)).toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()
    return hex === '000000' ? null : hex
  }
  return NAMED_COLORS[color] ?? null
}

/** The data: URI pictures (png/jpeg/gif) of Word clipboard HTML, in document order, without duplicates. */
export function wordPictures(html, { parse = defaultParse } = {}) {
  if (!isWordHtml(html)) return []
  const doc = parse(html)
  const seen = new Set()
  const pictures = []
  for (const image of doc.querySelectorAll('img')) {
    const src = image.getAttribute('src') ?? ''
    const type = /^data:(image\/[a-z]+)[;,]/i.exec(src)?.[1]?.toLowerCase()
    if (!type || !PICTURE_TYPES.includes(type) || seen.has(src)) continue
    seen.add(src)
    pictures.push({ src, type, width: Number(image.getAttribute('width')) || null, height: Number(image.getAttribute('height')) || null })
  }
  return pictures
}

/**
 * A copied picture or chart on its own: HTML (Word's or a browser's) with no
 * text and at most one picture. The clipboard's image file is then the
 * paste; with text around it, the HTML is.
 */
export function isSinglePictureHtml(html, { parse = defaultParse } = {}) {
  if (!html) return true
  const doc = parse(html)
  removeComments(doc.body)
  doc.body.querySelectorAll('style, script, title, meta, link').forEach((element) => element.remove())
  for (const shape of [...doc.body.getElementsByTagName('v:shapetype')]) shape.remove()
  const text = (doc.body.textContent ?? '').replace(/\s+/g, '')
  const pictures = Math.max(doc.body.querySelectorAll('img').length, doc.body.getElementsByTagName('v:shape').length)
  return text === '' && pictures <= 1 && doc.body.querySelectorAll('table').length === 0
}

function removeComments(root) {
  const walker = root.ownerDocument.createTreeWalker(root, 128 /* NodeFilter.SHOW_COMMENT */)
  const comments = []
  while (walker.nextNode()) comments.push(walker.currentNode)
  comments.forEach((comment) => comment.remove())
}

/** `@list l0:level2 {mso-level-number-format:bullet; …}` → { 'l0:2': 'bullet' }. A level without a format is decimal. */
function listKinds(doc) {
  const kinds = {}
  for (const style of doc.querySelectorAll('style')) {
    const css = style.textContent ?? ''
    for (const match of css.matchAll(/@list\s+(l\d+):level(\d+)\s*\{([^}]*)\}/gi)) {
      kinds[`${match[1]}:${match[2]}`] = /mso-level-number-format\s*:\s*(?:bullet|image)/i.test(match[3]) ? 'bullet' : 'ordered'
    }
  }
  return kinds
}

/** A marker glyph that is a bullet rather than a number or letter. */
function isBulletMarker(text) {
  const marker = (text ?? '').replace(/\s+/g, '')
  return marker === '' || /^[·•▪■□◦○●oO§\-–—*➢➤✓✔]$/.test(marker)
}

/**
 * Removes Word's downlevel islands `<![if !condition]>…<![endif]>` under
 * root (the HTML parser turns the two markers into bogus comments around the
 * island's nodes) and returns the text they held.
 */
function removeIslands(root, condition) {
  const opening = new RegExp(`^\\[if !${condition}\\]`, 'i')
  const walker = root.ownerDocument.createTreeWalker(root, 128 /* NodeFilter.SHOW_COMMENT */)
  const openers = []
  while (walker.nextNode()) {
    if (opening.test(walker.currentNode.data)) openers.push(walker.currentNode)
  }
  let text = ''
  for (const opener of openers) {
    let next = opener.nextSibling
    opener.remove()
    while (next && !(next.nodeType === 8 && /^\[endif\]/i.test(next.data))) {
      const following = next.nextSibling
      text += next.textContent ?? ''
      next.remove()
      next = following
    }
    next?.remove()
  }
  return text
}

/** Removes the marker Word writes in front of a list paragraph and returns its text. */
function stripListMarker(paragraph) {
  let marker = removeIslands(paragraph, 'supportLists')
  for (const ignored of paragraph.querySelectorAll('[style*="mso-list:Ignore" i], [style*="mso-list: Ignore" i]')) {
    marker += ignored.textContent ?? ''
    ignored.remove()
  }
  return marker
}

/** `mso-list:l0 level2 lfo1` → { list: 'l0', level: 2 }, or null. */
function listInfo(element) {
  const value = styleValue(element, 'mso-list')
  const match = value ? /(l\d+)\s+level(\d+)/i.exec(value) : null
  return match ? { list: match[1].toLowerCase(), level: Number(match[2]) } : null
}

/** Mac Word wraps some list items in their own <ol>/<ul>: unwrap them into list paragraphs so all items read alike. */
function unwrapWordLists(doc) {
  for (const list of [...doc.body.querySelectorAll('ol, ul')]) {
    const items = [...list.children]
    if (!items.length || !items.every((item) => item.tagName === 'LI' && listInfo(item))) continue
    for (const item of items) {
      const paragraph = doc.createElement('p')
      for (const attribute of [...item.attributes]) paragraph.setAttribute(attribute.name, attribute.value)
      paragraph.setAttribute('data-word-list-kind', list.tagName === 'UL' ? 'bullet' : 'ordered')
      while (item.firstChild) paragraph.appendChild(item.firstChild)
      list.parentNode.insertBefore(paragraph, list)
    }
    list.remove()
  }
}

/** The next element sibling, skipping whitespace text and comments. */
function nextElement(node) {
  let next = node.nextSibling
  while (next && (next.nodeType === 8 || (next.nodeType === 3 && !next.data.replace(/\s+/g, '')))) next = next.nextSibling
  return next?.nodeType === 1 ? next : null
}

/** Consecutive list paragraphs → nested <ul>/<ol>, each item an <li><p>. */
function buildLists(doc, kinds) {
  const done = new Set()
  for (const first of [...doc.body.querySelectorAll('p, h1, h2, h3, h4, h5, h6')]) {
    if (done.has(first) || !listInfo(first)) continue
    const run = []
    for (let element = first; element && listInfo(element) && !done.has(element); element = nextElement(element)) {
      run.push(element)
      done.add(element)
    }

    // Outermost lists go in front of this marker, in order; nested ones into their parent item.
    const anchor = doc.createComment('list')
    first.parentNode.insertBefore(anchor, first)
    const stack = []
    for (const paragraph of run) {
      const { list, level } = listInfo(paragraph)
      const marker = stripListMarker(paragraph)
      const kind = paragraph.getAttribute('data-word-list-kind') ?? kinds[`${list}:${level}`] ?? (isBulletMarker(marker) ? 'bullet' : 'ordered')
      paragraph.removeAttribute('data-word-list-kind')

      while (stack.length && stack[stack.length - 1].level > level) stack.pop()
      let top = stack[stack.length - 1]
      if (top && top.level === level && top.kind !== kind) {
        stack.pop()
        top = stack[stack.length - 1]
      }
      if (!top || top.level < level) {
        const element = doc.createElement(kind === 'bullet' ? 'ul' : 'ol')
        const parentItem = top?.element.lastElementChild
        if (parentItem) {
          parentItem.appendChild(element)
        } else {
          anchor.parentNode.insertBefore(element, anchor)
        }
        top = { level, kind, element }
        stack.push(top)
      }

      const item = doc.createElement('li')
      const body = doc.createElement('p')
      const align = paragraph.getAttribute('data-ooxml-align')
      if (align) body.setAttribute('data-ooxml-align', align)
      while (paragraph.firstChild) body.appendChild(paragraph.firstChild)
      item.appendChild(body)
      top.element.appendChild(item)
      paragraph.remove()
    }
    anchor.remove()
  }
}

/** Colour and highlight → data-ooxml-* on spans, inherited values merged in (an inner span's mark replaces the outer one). */
function markRuns(element, inherited) {
  for (const child of [...element.children]) {
    const colorStyle = styleValue(child, 'color')
    const highlightStyle = styleValue(child, 'mso-highlight')
    const own = {
      color: colorStyle === null ? undefined : cssColorToHex(colorStyle),
      highlight: highlightStyle === null ? undefined : (HIGHLIGHTS[highlightStyle.toLowerCase()] ?? null),
    }
    const next = {
      color: own.color === undefined ? inherited.color : own.color,
      highlight: own.highlight === undefined ? inherited.highlight : own.highlight,
    }
    // Automatic colour (or no highlight) inside a coloured (highlighted) span resets it: 'auto' / 'none'.
    const color = next.color ?? (inherited.color ? 'auto' : null)
    const highlight = next.highlight ?? (inherited.highlight ? 'none' : null)
    const changes = own.color !== undefined || own.highlight !== undefined
    if (changes && INLINE_TAGS.includes(child.tagName) && (color || highlight)) {
      let span = child
      if (child.tagName !== 'SPAN') {
        span = child.ownerDocument.createElement('span')
        while (child.firstChild) span.appendChild(child.firstChild)
        child.appendChild(span)
      }
      if (color) span.setAttribute('data-ooxml-color', color)
      if (highlight) span.setAttribute('data-ooxml-highlight', highlight)
    }
    markRuns(child, next)
  }
}

/** text-align / align → data-ooxml-align on paragraphs and headings. */
function markAlignment(doc) {
  for (const block of doc.body.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li')) {
    const value = (styleValue(block, 'text-align') ?? block.getAttribute('align') ?? '').toLowerCase()
    if (ALIGNMENTS[value]) block.setAttribute('data-ooxml-align', ALIGNMENTS[value])
  }
}

function renameElement(element, tagName) {
  const renamed = element.ownerDocument.createElement(tagName)
  for (const attribute of [...element.attributes]) renamed.setAttribute(attribute.name, attribute.value)
  while (element.firstChild) renamed.appendChild(element.firstChild)
  element.replaceWith(renamed)
  return renamed
}

/**
 * A cell's width as a weight for colwidth: px, pt (→ px) or a percentage
 * (×10; only the proportions matter, contract §4), or 0.
 */
function cellWidth(cell) {
  for (const value of [styleValue(cell, 'width'), cell.getAttribute('width')]) {
    const match = /^([\d.]+)\s*(pt|px|%)?$/.exec((value ?? '').trim())
    if (!match || !(Number(match[1]) > 0)) continue
    const number = Number(match[1])
    return match[2] === 'pt' ? number * (96 / 72) : match[2] === '%' ? number * 10 : number
  }
  return 0
}

/**
 * Header rows → <th>, fill → data-ooxml-fill, width → colwidth. The cells
 * Word adds for a row that skips grid columns (`mso-cell-special:placeholder`
 * and the `<![if !supportMisalignedRows]>` spacers) go: a pasted table is a
 * new one, with a full grid.
 */
function markTables(doc) {
  removeIslands(doc.body, 'supportMisalignedRows')
  doc.body.querySelectorAll('td[style*="mso-cell-special:placeholder" i]').forEach((cell) => cell.remove())
  for (const cell of [...doc.body.querySelectorAll('thead td')]) renameElement(cell, 'th')
  for (const cell of doc.body.querySelectorAll('td, th')) {
    const fill = cssColorToHex(styleValue(cell, 'background') ?? styleValue(cell, 'background-color') ?? cell.getAttribute('bgcolor'))
    if (fill) cell.setAttribute('data-ooxml-fill', fill)
    const width = cellWidth(cell)
    const span = Math.max(1, Number(cell.getAttribute('colspan')) || 1)
    if (width > 0 && !cell.hasAttribute('colwidth')) {
      cell.setAttribute('colwidth', Array.from({ length: span }, () => Math.max(1, Math.round(width / span))).join(','))
    }
  }
}

/**
 * Pictures: one that was uploaded (its data: URI is a key of `pictures`)
 * becomes `<img data-studio-picture>`, the rest are dropped and counted.
 */
function markPictures(doc, pictures) {
  let dropped = 0
  // VML outside a conditional comment (a Mac Word chart with external links):
  // a file:// picture, unless an <img> stands in for it.
  const stoodIn = new Set([...doc.body.querySelectorAll('img')].map((image) => image.getAttribute('v:shapes')).filter(Boolean))
  for (const shape of [...doc.body.getElementsByTagName('v:shape')]) {
    if (!stoodIn.has(shape.getAttribute('id'))) dropped++
    shape.remove()
  }
  for (const shape of [...doc.body.getElementsByTagName('v:shapetype')]) shape.remove()
  for (const image of [...doc.body.querySelectorAll('img')]) {
    const picture = pictures.get(image.getAttribute('src') ?? '')
    if (picture) {
      const marker = doc.createElement('img')
      marker.setAttribute('data-studio-picture', JSON.stringify(picture))
      image.replaceWith(marker)
    } else {
      dropped++
      image.remove()
    }
  }
  return dropped
}

/**
 * Word clipboard HTML → HTML for the Studio parse rules.
 *
 * @param {string} html
 * @param {{ pictures?: Map<string, object>, parse?: (html: string) => Document }} [options]
 *   pictures: data: URI → the templateImage attrs it was uploaded as.
 * @returns {{ html: string, droppedPictures: number }}
 */
export function transformWordHtml(html, { pictures = new Map(), parse = defaultParse } = {}) {
  const doc = parse(html)
  const kinds = listKinds(doc)

  // Word's islands that have no place in the document.
  doc.body
    .querySelectorAll('style, script, title, meta, link, xml, [style*="mso-element:footnote-list" i], [style*="mso-element:comment-list" i], [style*="mso-element:endnote-list" i], .MsoCommentReference')
    .forEach((element) => element.remove())
  for (const reference of [...doc.body.querySelectorAll('a[href^="#_ftn"], a[href^="#_edn"], a[href^="#_msocom"]')]) reference.remove()
  removeIslands(doc.body, 'supportEmptyParas')
  for (const element of [...doc.body.getElementsByTagName('o:p')]) {
    if (!(element.textContent ?? '').replace(/\s+/g, '')) element.remove()
  }

  markAlignment(doc)
  unwrapWordLists(doc)
  buildLists(doc, kinds)
  markTables(doc)
  markRuns(doc.body, { color: null, highlight: null })
  const droppedPictures = markPictures(doc, pictures)
  removeComments(doc.body)

  return { html: doc.body.innerHTML, droppedPictures }
}
